import { createNoise2D, NoiseFunction2D } from 'simplex-noise';
import { QuadTree } from './QuadTree';
import { Rectangle } from './Rectangle';
import { map } from './util';

type PointConnection = {
  to: Point;
  distance: number;
  maxDistance: number;
};

export class Point {
  public position: { x: number; y: number };
  private moveSpeed: number;
  private pointSize: number;
  private arcDistance: number;
  private backgroundSize: { x: number; y: number };
  private direction: { x: number; y: number };
  public quadtree: QuadTree | null = null;

  public connection: PointConnection | null = null;

  constructor(
    private noise: NoiseFunction2D,
    private screen: Rectangle,
    backgroundSizeX: number,
    backgroundSizeY: number,
    moveSpeed: number,
    pointSize: number,
    private rootQuadtree: QuadTree,
  ) {
    // Store properties

    this.moveSpeed = moveSpeed / 2 + Math.random() * (moveSpeed / 1.5);
    this.pointSize = pointSize;
    this.backgroundSize = { x: backgroundSizeX, y: backgroundSizeY };

    // Generate random starting position
    this.position = {
      x: screen.x + Math.floor(Math.random() * (screen.w - 1)),
      y: screen.y + Math.floor(Math.random() * (screen.h - 1)),
    };

    // Generate random starting direction
    const xDir = Math.random() * 2 - 1;
    const yDir = (Math.random() * 2 - 1) / 1.5;

    // Convert to a unit vector
    const length = Math.sqrt(xDir ** 2 + yDir ** 2);
    this.direction = { x: xDir / length, y: yDir / length };

    this.arcDistance = this.pointSize * 4;
  }

  draw(ctx: CanvasRenderingContext2D) {
    const alpha = map(this.position.y, 0, this.backgroundSize.y, 255, 0);
    ctx.strokeStyle = `rgba(200, 200, 200, ${alpha})`;
    ctx.fillStyle = `rgba(200, 200, 200, ${alpha})`;

    ctx.beginPath();
    ctx.arc(this.position.x, this.position.y, this.pointSize, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();

    if (this.connection) {
      ctx.beginPath();

      const alpha = map(
        this.connection.distance,
        0,
        this.connection.maxDistance,
        1,
        0,
      );
      ctx.strokeStyle = `rgba(200, 200, 200, ${alpha})`;
      ctx.fillStyle = `rgba(200, 200, 200, ${alpha})`;

      ctx.moveTo(this.position.x + this.arcDistance, this.position.y);

      ctx.arc(
        this.position.x,
        this.position.y,
        this.arcDistance,
        0,
        Math.PI * 2,
      );

      ctx.moveTo(this.position.x, this.position.y);

      if (this.position.x < this.connection.to.position.x) {
        ctx.lineTo(
          this.connection.to.position.x,
          this.connection.to.position.y,
        );
      }
      ctx.stroke();
    }
  }

  move() {
    // Update the position of the object
    const noiseValue = this.noise(
      this.position.x * 0.01,
      this.position.y * 0.01,
    );

    const x =
      this.direction.x * Math.cos(noiseValue) -
      this.direction.y * Math.sin(noiseValue);
    const y =
      this.direction.x * Math.sin(noiseValue) +
      this.direction.y * Math.cos(noiseValue);

    this.position.x += x * this.moveSpeed;
    this.position.y += y * this.moveSpeed;

    // noiseTimeX += 0.1;
    // noiseTimeY += 0.1;

    // Handle going off the left & right sides of the screen
    if (this.position.x <= this.screen.x + 1)
      this.direction.x = Math.abs(this.direction.x);
    else if (this.position.x > this.screen.w - 2)
      this.direction.x = -Math.abs(this.direction.x);

    // Handle going off the top and bottom sides of the screen
    if (this.position.y < this.screen.y + 1)
      this.direction.y = Math.abs(this.direction.y);
    else if (this.position.y > this.screen.h - 2)
      this.direction.y = -Math.abs(this.direction.y);

    // Reallocate the point if it moves outside its current QuadTree boundary
    if (this.quadtree) {
      if (!this.quadtree.boundary.contains(this)) {
        this.quadtree.remove(this);
        this.rootQuadtree.insert(this);
      }
    } else {
      this.rootQuadtree.insert(this);
    }
  }

  connect(maxLineDist: number) {
    const range = new Rectangle(
      this.position.x,
      this.position.y,
      maxLineDist,
      maxLineDist,
    );
    const pointsInRange = this.rootQuadtree.query(range);
    for (const point of pointsInRange) {
      const distanceBetween = this.distance(point);
      if (
        distanceBetween > 0 &&
        distanceBetween < maxLineDist &&
        (!this.connection || this.connection.to === point) &&
        (!point.connection || point.connection.to === this)
      ) {
        this.connection = {
          to: point,
          distance: distanceBetween,
          maxDistance: maxLineDist,
        };
        point.connection = {
          to: this,
          distance: distanceBetween,
          maxDistance: maxLineDist,
        };
      } else if (
        this.connection &&
        this.connection.to === point &&
        distanceBetween > maxLineDist
      ) {
        this.connection = null;
        point.connection = null;
      }
    }
  }

  distance(pointB: Point) {
    const pointA = this;
    const diffX = pointA.position.x - pointB.position.x;
    const diffY = (pointA.position.y - pointB.position.y) * 2; // Multiplicando a diferença no eixo Y por 2 para aumentar a inclinação
    return Math.sqrt(diffX * diffX + diffY * diffY);
  }
}
